import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { Typography, Box, Skeleton, Avatar, Chip, Tabs, Tab, TextField } from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import ChatOutlinedIcon from "@mui/icons-material/ChatOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import EventRepeatOutlinedIcon from "@mui/icons-material/EventRepeatOutlined";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { Order, OrderStatus } from "../../../../Models/Order";
import { OrderAPIHandler } from "../../../../services/NetworkClient/BackendAPIs/OrderAPIHandler";
import { formatCurrencyIndian } from "../../../../utils/MoneyConversionUtils";
import { getDateObjForCalendar, getDateFromTimeStamp } from "../../../../utils/DateAndTimeUtils";
import GGButton from "../../../CommonComponents/GGButton/GGButton";
import GGLoaderButton from "../../../CommonComponents/GGLoaderButton/GGLoaderButton";
import GGModalWindow from "../../../UtilityComponents/GGModalWindow/GGModalWindow";
import { ProgressReviewAPIHandler } from "../../../../services/NetworkClient/BackendAPIs/ProgressReviewAPIHandler";
import { ReviewAPIHandler } from "../../../../services/NetworkClient/BackendAPIs/ReviewAPIHandler";
import { Review } from "../../../../Models/Review";
import StarRating from "../../../CommonComponents/StarRating/StarRating";
import SnackBar, { SnackTypes } from "../../../UtilityComponents/SnackBar/SnackBar";
import Inventory2RoundedIcon from '@mui/icons-material/Inventory2Rounded';
import './OrdersPanel.scss';
import { ErrorLogger } from '../../../../utils/Logger';

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

const TabPanel = (props: TabPanelProps) => {
    const { children, value, index, ...other } = props;
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`orders-tabpanel-${index}`}
            aria-labelledby={`orders-tab-${index}`}
            {...other}
        >
            {value === index && <Box>{children}</Box>}
        </div>
    );
};

const OrderCard = ({ order, isBuyerOrder }: { order: Order; isBuyerOrder: boolean }) => {
    const navigate = useNavigate();
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [isExpanded, setIsExpanded] = useState(false);
    const [showOrderSummary, setShowOrderSummary] = useState<boolean>(false);
    const [invoiceDownloadLoader, setInvoiceDownloadLoader] = useState(false);
    const [receiptDownloadLoader, setReceiptDownloadLoader] = useState(false);
    const [isAskForProgressReviewBtnDisabled, setIsAskForProgressReviewBtnDisabled] = useState(false);
    const [reviewComments, setReviewComments] = useState("");
    const [rating, setRating] = useState(0);
    const [isSubmittingReview, setIsSubmittingReview] = useState(false);
    const [showReviewModal, setShowReviewModal] = useState(false);
    const [showRatingFormModal, setShowRatingFormModal] = useState(false);
    
    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);
    const snackBarRef = useRef<{ showSnackbar(): any }>(null);
    const [snackBarParams, setSnackBarParams] = useState({
        message: "",
        state: SnackTypes.error,
    });

    const showSnackbar = useCallback((message: string, state: SnackTypes) => {
        setSnackBarParams({ message: message, state: state });
        snackBarRef.current?.showSnackbar();
    }, []);
    
    const deliveryDate = order.orderCompletionTimeStamp 
        ? getDateObjForCalendar(new Date(order.orderCompletionTimeStamp))
        : order.deliveryTimeStamp 
        ? getDateObjForCalendar(new Date(order.deliveryTimeStamp))
        : null;
    
    const orderPlacedDate = order.createdAt ? getDateFromTimeStamp(order.createdAt) : '';
    
    const totalAmount = order.orderSummary?.orderTotals?.totalAmountPayableByBuyer || 0;
    // const netAmount = order.orderSummary?.orderTotals?.totalNetAmountReleasedToSeller || 0;
    
    const getGrandTotalInINR = () => {
        if (isBuyerOrder) {
            let totalPaid = order.orderSummary?.orderTotals?.totalAmountPayableByBuyer ?? 0;
            return formatCurrencyIndian(totalPaid);
        } else {
            let amountPayable = order.orderSummary?.orderTotals?.totalNetAmountReleasedToSeller ?? 0;
            return formatCurrencyIndian(amountPayable);
        }
    };
    
    const orderSummaryDetailsBtnHandler = () => {
        setShowOrderSummary(!showOrderSummary);
    };

    const closeOrderSummaryModal = () => {
        setShowOrderSummary(false);
    };

    const downloadInvoice = async (orderId: string) => {
        try {
            setInvoiceDownloadLoader(true);
            const blob = await OrderAPIHandler.shared.generateTaxInvoiceForSellerAPI(orderId);
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `Order_${orderId}_Seller_Invoice.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            ErrorLogger.log(err, 'NewOrdersPanel.OrderCard.downloadSellerInvoice');
        } finally {
            setInvoiceDownloadLoader(false);
        }
    };

    const downloadSellerReceipt = async (orderId: string) => {
        try {
            setReceiptDownloadLoader(true);
            const blob = await OrderAPIHandler.shared.downloadOrderReceiptPdfForSeller(orderId);
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `Order_${orderId}_Seller_Receipt.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            ErrorLogger.log(err, 'NewOrdersPanel.OrderCard.downloadSellerReceipt');
        } finally {
            setReceiptDownloadLoader(false);
        }
    };

    const downloadBuyerInvoice = async (orderId: string) => {
        try {
            setInvoiceDownloadLoader(true);
            const blob = await OrderAPIHandler.shared.generateTaxInvoiceForBuyerAPI(orderId);
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `Order_${orderId}_Invoice.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            ErrorLogger.log(err, 'NewOrdersPanel.OrderCard.downloadBuyerInvoice');
        } finally {
            setInvoiceDownloadLoader(false);
        }
    };

    const downloadBuyerReceipt = async (orderId: string) => {
        try {
            setReceiptDownloadLoader(true);
            const blob = await OrderAPIHandler.shared.downloadOrderReceiptPdfForBuyer(orderId);
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `Order_${orderId}_Receipt.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            ErrorLogger.log(err, 'NewOrdersPanel.OrderCard.downloadBuyerReceipt');
        } finally {
            setReceiptDownloadLoader(false);
        }
    };

    const askForProgressReview = async (orderId: string) => {
        try {
            const message = await ProgressReviewAPIHandler.shared.askForProgressReview(orderId);
            showSnackbar(
                message ?? "Freelancer has been asked for a progress review.",
                SnackTypes.success
            );
            setIsAskForProgressReviewBtnDisabled(true);
        } catch (error) {
            ErrorLogger.log(error, 'NewOrdersPanel.OrderCard.askForProgressReview');
            showSnackbar(error as string, SnackTypes.error);
        }
    };

    const handleRatingReviewSubmit = useCallback(async () => {
        if (rating === 0) {
            showSnackbar("Please provide a rating!", SnackTypes.error);
            return;
        }
        if (reviewComments.trim() === "") {
            showSnackbar("Please provide a review!", SnackTypes.error);
            return;
        }

        const reviewData = {
            gigId: order.metaData.gig.id,
            order: order.id,
            rating: rating,
            attachments: [],
            comment: reviewComments,
        };

        setIsSubmittingReview(true);
        ReviewAPIHandler.shared
            .createReview(reviewData)
            .then(() => {
                showSnackbar("Review submitted successfully!", SnackTypes.success);
                setIsSubmittingReview(false);
                setShowRatingFormModal(false);
                window.location.reload();
            })
            .catch((error) => {
                setIsSubmittingReview(false);
                showSnackbar(
                    (error as string) || "Failed to submit review!",
                    SnackTypes.error
                );
            });
    }, [rating, reviewComments, order.metaData.gig.id, order.id, showSnackbar]);
    
    const OrderSummaryModalContent = isBuyerOrder ? (
        <div className="order-summary-modal-content">
            <table className="order-summary-table" style={{ width: "100%" }}>
                <tbody>
                    {/* Main Order Breakdown Section */}
                    <tr>
                        <td className="order-summary-section-title" colSpan={2}>
                            <b>Main Order Breakdown</b>
                        </td>
                    </tr>
                    
                    {/* Primary Transaction */}
                    <tr>
                        <td className="order-summary-item-label">Gig Amount</td>
                        <td className="order-summary-item-value">{formatCurrencyIndian(order.orderSummary?.primaryTransaction?.basePrice ?? 0)}</td>
                    </tr>
                    
                    {/* Platform Fee */}
                    <tr>
                        <td className="order-summary-item-label">Platform Fee ({(order.orderSummary?.primaryTransaction?.buyerFinancials?.platformFee?.percentage ?? 0) * 100}%)</td>
                        <td className="order-summary-item-value">{formatCurrencyIndian(order.orderSummary?.primaryTransaction?.buyerFinancials?.platformFee?.actualFeeCharged ?? 0)}</td>
                    </tr>
                    
                    {/* GST */}
                    <tr>
                        <td className="order-summary-item-label">GST @ {(order.orderSummary?.primaryTransaction?.buyerFinancials?.gstDetails?.ratePercentage ?? 0) * 100}%</td>
                        <td className="order-summary-item-value">{formatCurrencyIndian(order.orderSummary?.primaryTransaction?.buyerFinancials?.gstDetails?.actualGstAmount ?? 0)}</td>
                    </tr>
                    
                    {/* Promo Discount if applicable */}
                    {order.orderSummary?.primaryTransaction?.promoDetails?.promoCode && (
                        <tr>
                            <td className="order-summary-item-label">Promo Applied ({order.orderSummary.primaryTransaction.promoDetails.promoCode})</td>
                            <td className="order-summary-item-value promo-code">- {formatCurrencyIndian(order.orderSummary.primaryTransaction.promoDetails.actualDiscountValue ?? 0)}</td>
                        </tr>
                    )}
                    
                    {/* Main Order Subtotal */}
                    <tr className="order-summary-subtotal-row">
                        <td className="order-summary-subtotal-title"><b>Main Order Subtotal</b></td>
                        <td className="order-summary-subtotal-value" style={{ textAlign: "right" }}><b>{formatCurrencyIndian(order.orderSummary?.primaryTransaction?.buyerFinancials?.totalAmountPayable ?? 0)}</b></td>
                    </tr>
                    
                    {/* Revision Transactions */}
                    {order.orderSummary?.revisionTransactions && order.orderSummary.revisionTransactions.length > 0 && (
                        <>
                            <tr>
                                <td colSpan={2}>
                                    <hr className="order-summary-section-divider" />
                                </td>
                            </tr>
                            <tr>
                                <td className="order-summary-section-title" colSpan={2}>
                                    <b>Revision Transactions</b>
                                </td>
                            </tr>
                            
                            {/* All Revisions with proper numbering */}
                            {order.orderSummary.revisionTransactions.map((revision, index) => {
                                const isFreeRevision = revision.buyerFinancials?.totalAmountPayable === 0;
                                const revisionNumber = index + 1;
                                
                                return (
                                    <React.Fragment key={revision.transactionId}>
                                        <tr className="revision-spacer">
                                            <td colSpan={2}></td>
                                        </tr>
                                        
                                        {isFreeRevision ? (
                                            <tr>
                                                <td className="order-summary-item-label">Included Revision #{revisionNumber}</td>
                                                <td className="order-summary-item-value free">FREE</td>
                                            </tr>
                                        ) : (
                                            <>
                                                <tr>
                                                    <td className="order-summary-item-label">Paid Revision #{revisionNumber} - Base Amount</td>
                                                    <td className="order-summary-item-value">{formatCurrencyIndian(revision.basePrice)}</td>
                                                </tr>
                                                <tr>
                                                    <td className="order-summary-item-label">Platform Fee ({((revision.buyerFinancials?.platformFee?.percentage ?? 0) * 100)}%)</td>
                                                    <td className="order-summary-item-value">{formatCurrencyIndian(revision.buyerFinancials?.platformFee?.actualFeeCharged ?? 0)}</td>
                                                </tr>
                                                <tr>
                                                    <td className="order-summary-item-label">GST @ {((revision.buyerFinancials?.gstDetails?.ratePercentage ?? 0) * 100)}%</td>
                                                    <td className="order-summary-item-value">{formatCurrencyIndian(revision.buyerFinancials?.gstDetails?.actualGstAmount ?? 0)}</td>
                                                </tr>
                                                {revision.promoDetails?.promoCode && (
                                                    <tr>
                                                        <td className="order-summary-item-label">Promo Applied ({revision.promoDetails.promoCode})</td>
                                                        <td className="order-summary-item-value promo-code">- {formatCurrencyIndian(revision.promoDetails.actualDiscountValue ?? 0)}</td>
                                                    </tr>
                                                )}
                                                <tr className="order-summary-revision-subtotal-row">
                                                    <td className="order-summary-revision-subtotal-title"><b>Revision #{revisionNumber} Subtotal</b></td>
                                                    <td className="order-summary-revision-subtotal-value" style={{ textAlign: "right" }}><b>{formatCurrencyIndian(revision.buyerFinancials?.totalAmountPayable ?? 0)}</b></td>
                                                </tr>
                                            </>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </>
                    )}
                    
                    <tr>
                        <td colSpan={2}>
                            <hr className="order-summary-divider" />
                        </td>
                    </tr>
                    <tr className="order-summary-grand-total-row">
                        <td className="order-summary-grand-total-title"><b>Total Amount Paid</b></td>
                        <td className="order-summary-grand-total-value" style={{ textAlign: "right" }}><b>{getGrandTotalInINR()}</b></td>
                    </tr>
                </tbody>
            </table>
            <br></br>
        </div>
    ) : (
        <div className="order-summary-modal-content">
            <table className="order-summary-table" style={{ width: "100%" }}>
                <tbody>
                    {/* Main Order Breakdown Section */}
                    <tr>
                        <td className="order-summary-section-title" colSpan={2}>
                            <b>Main Order Breakdown</b>
                        </td>
                    </tr>
                    
                    {/* Primary Transaction */}
                    <tr>
                        <td className="order-summary-item-label">Gig Amount</td>
                        <td className="order-summary-item-value">{formatCurrencyIndian(order.orderSummary?.primaryTransaction?.basePrice ?? 0)}</td>
                    </tr>
                    
                    {/* Platform Fee Deduction */}
                    <tr>
                        <td className="order-summary-item-label">Platform Fee ({(order.orderSummary?.primaryTransaction?.sellerFinancials?.platformFee?.percentage ?? 0) * 100}%)</td>
                        <td className="order-summary-item-value deduction">- {formatCurrencyIndian(order.orderSummary?.primaryTransaction?.sellerFinancials?.platformFee?.actualFeeCharged ?? 0)}</td>
                    </tr>
                    
                    {/* GST Deduction */}
                    <tr>
                        <td className="order-summary-item-label">GST @ {(order.orderSummary?.primaryTransaction?.sellerFinancials?.gstDetails?.ratePercentage ?? 0) * 100}%</td>
                        <td className="order-summary-item-value deduction">- {formatCurrencyIndian(order.orderSummary?.primaryTransaction?.sellerFinancials?.gstDetails?.actualGstAmount ?? 0)}</td>
                    </tr>
                    
                    {/* Main Order Subtotal */}
                    <tr className="order-summary-subtotal-row">
                        <td className="order-summary-subtotal-title"><b>Main Order Subtotal</b></td>
                        <td className="order-summary-subtotal-value" style={{ textAlign: "right" }}><b>{formatCurrencyIndian(order.orderSummary?.primaryTransaction?.sellerFinancials?.totalAmountPayable ?? 0)}</b></td>
                    </tr>
                    
                    {/* Revision Transactions */}
                    {order.orderSummary?.revisionTransactions && order.orderSummary.revisionTransactions.length > 0 && (
                        <>
                            <tr>
                                <td colSpan={2}>
                                    <hr className="order-summary-section-divider" />
                                </td>
                            </tr>
                            <tr>
                                <td className="order-summary-section-title" colSpan={2}>
                                    <b>Revision Transactions</b>
                                </td>
                            </tr>
                            
                            {/* All Revisions with proper numbering */}
                            {order.orderSummary.revisionTransactions.map((revision, index) => {
                                const isFreeRevision = revision.buyerFinancials?.totalAmountPayable === 0;
                                const revisionNumber = index + 1;
                                
                                return (
                                    <React.Fragment key={revision.transactionId}>
                                        <tr className="revision-spacer">
                                            <td colSpan={2}></td>
                                        </tr>
                                        
                                        {isFreeRevision ? (
                                            <tr>
                                                <td className="order-summary-item-label">Included Revision #{revisionNumber}</td>
                                                <td className="order-summary-item-value free">FREE</td>
                                            </tr>
                                        ) : (
                                            <>
                                                <tr>
                                                    <td className="order-summary-item-label">Paid Revision #{revisionNumber} - Base Amount</td>
                                                    <td className="order-summary-item-value">{formatCurrencyIndian(revision.basePrice)}</td>
                                                </tr>
                                                <tr>
                                                    <td className="order-summary-item-label">Platform Fee ({((revision.sellerFinancials?.platformFee?.percentage ?? 0) * 100)}%)</td>
                                                    <td className="order-summary-item-value deduction">- {formatCurrencyIndian(revision.sellerFinancials?.platformFee?.actualFeeCharged ?? 0)}</td>
                                                </tr>
                                                <tr>
                                                    <td className="order-summary-item-label">GST @ {((revision.sellerFinancials?.gstDetails?.ratePercentage ?? 0) * 100)}%</td>
                                                    <td className="order-summary-item-value deduction">- {formatCurrencyIndian(revision.sellerFinancials?.gstDetails?.actualGstAmount ?? 0)}</td>
                                                </tr>
                                                <tr className="order-summary-revision-subtotal-row">
                                                    <td className="order-summary-revision-subtotal-title"><b>Revision #{revisionNumber} Subtotal</b></td>
                                                    <td className="order-summary-revision-subtotal-value" style={{ textAlign: "right" }}><b>{formatCurrencyIndian(revision.sellerFinancials?.totalAmountPayable ?? 0)}</b></td>
                                                </tr>
                                            </>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </>
                    )}
                    
                    <tr>
                        <td colSpan={2}>
                            <hr className="order-summary-divider" />
                        </td>
                    </tr>
                    <tr className="order-summary-grand-total-row">
                        <td className="order-summary-grand-total-title"><b>Total amount credited</b></td>
                        <td className="order-summary-grand-total-value" style={{ textAlign: "right" }}><b>{getGrandTotalInINR()}</b></td>
                    </tr>
                </tbody>
            </table>
            <br></br>
        </div>
    );
    
    const getStatusInfo = () => {
        switch (order.status) {
            case OrderStatus.completed:
            case OrderStatus.approvedByBuyer:
                return {
                    label: deliveryDate ? `Delivered ${deliveryDate.day} ${deliveryDate.month}` : 'Delivered',
                    message: 'Order completed successfully',
                    backgroundColor: '#1f6d1b',
                    textColor: '#ffffff'
                };
            case OrderStatus.pending:
                return {
                    label: 'Pending',
                    message: 'Order is being processed',
                    backgroundColor: '#fbbf24',
                    textColor: '#000000'
                };
            case OrderStatus.cancelled:
                return {
                    label: 'Cancelled',
                    message: 'This order has been cancelled',
                    backgroundColor: '#ef4444',
                    textColor: '#ffffff'
                };
            case OrderStatus.readyForDelivery:
                return {
                    label: 'Ready for Delivery',
                    message: 'Awaiting buyer approval',
                    backgroundColor: '#01c2ea',
                    textColor: '#ffffff'
                };
            case OrderStatus.overdue:
                return {
                    label: 'Overdue',
                    message: 'Delivery deadline has passed',
                    backgroundColor: '#dc2626',
                    textColor: '#ffffff'
                };
            default:
                return {
                    label: order.status,
                    message: '',
                    backgroundColor: '#6b7280',
                    textColor: '#ffffff'
                };
        }
    };

    const statusInfo = getStatusInfo();
    const isCompleted = order.status === OrderStatus.completed || order.status === OrderStatus.approvedByBuyer;

    const getDeliveryDateLabel = () => {
        switch (order.status) {
            case OrderStatus.pending:
                return { label: 'Expected by', color: 'inherit' };
            case OrderStatus.completed:
            case OrderStatus.approvedByBuyer:
                return { label: 'Delivered on', color: 'inherit' };
            case OrderStatus.overdue:
                return { label: 'Was due on', color: '#ef4444' };
            case OrderStatus.readyForDelivery:
                return { label: 'Expected by', color: 'inherit' };
            case OrderStatus.cancelled:
                return { label: 'Cancelled on', color: '#ef4444' };
            default:
                return { label: 'Delivery On', color: 'inherit' };
        }
    };

    const deliveryDateLabelInfo = getDeliveryDateLabel();

    const RatingsFormContent = useMemo(() => (
        <Box className="ratings-form-container">
            <StarRating
                className="star-rating-row"
                value={rating}
                onChange={setRating}
            />
            <TextField
                style={{ width: "100%" }}
                value={reviewComments}
                onChange={(e) => setReviewComments(e.target.value)}
                className="feedback-textarea"
                id="outlined-multiline-static"
                multiline
                fullWidth
                rows={2.5}
                placeholder="How did you like this service...?"
            />
            <GGLoaderButton
                className="submit-review-btn"
                variant="outlined"
                style={{ width: "fit-content", border: "none" }}
                isLoading={isSubmittingReview}
                onClick={handleRatingReviewSubmit}
            >
                Submit Review
            </GGLoaderButton>
        </Box>
    ), [rating, reviewComments, isSubmittingReview, handleRatingReviewSubmit]);

    const StaticRatings = ({ review }: { review: Review }) => {
        return (
            <Box className="rating-and-review-content">
                <Typography fontSize={"16px"} className="rating-and-review-text">
                    You have rated <b>{order.metaData.seller.fullName}</b>!
                </Typography>
                <StarRating
                    className="star-rating-row"
                    disabled={true}
                    value={review.rating}
                />
                <Box
                    flexDirection={"column"}
                    display={"flex"}
                    padding={"20px"}
                    width={"100%"}
                    sx={{ border: "1px solid rgb(215, 215, 215)", borderRadius: "8px"}}
                >
                    <Typography
                        fontSize={"12px"}
                        fontWeight={1000}
                        marginBottom={"10px"}
                    >
                        Comments
                    </Typography>
                    <Typography fontSize={"16px"}>{review.comment}</Typography>
                </Box>
            </Box>
        );
    };

    return (
        <>
            <SnackBar
                ref={snackBarRef}
                severity={snackBarParams.state}
                alertDuration={5000}
                message={snackBarParams.message}
            />
            <Box className={`order-card ${isMobile ? 'mobile-card' : ''} ${isMobile && isExpanded ? 'expanded' : ''}`}>
            {!isMobile && (
            <div className="order-card-header">
                <div className="order-header-left">
                    <Typography variant="caption" className="order-label">ORDER PLACED</Typography>
                    <Typography variant="body2" className="order-value">{orderPlacedDate}</Typography>
                </div>
                <div className="order-header-center">
                    <Typography variant="caption" className="order-label">TOTAL</Typography>
                    <Typography variant="body2" className="order-value">{ isBuyerOrder ? formatCurrencyIndian(totalAmount) : formatCurrencyIndian(order.orderSummary?.orderTotals?.totalNetAmountReleasedToSeller ?? 0) }</Typography>
                </div>
                <div className="order-header-right">
                    <Typography variant="caption" className="order-label">ORDER # {order.id.substring(order.id.length - 8)}</Typography>
                    <div className="order-links">
                        {isBuyerOrder && isCompleted && (
                            <Typography variant="caption" className="link-text" onClick={() => {
                                navigate(`/order-details/${order.id}`);
                            }}>
                                View order details
                            </Typography>
                        )}
                        <Typography variant="caption" className="link-text" onClick={orderSummaryDetailsBtnHandler}>
                            Order Summary
                        </Typography>
                    </div>
                </div>
            </div>
            )}
            
            {/* Mobile Collapsed View */}
            {isMobile && !isExpanded && (
                <div className="mobile-card-collapsed" onClick={() => setIsExpanded(true)}>
                    <div className="mobile-collapsed-content">
                        <img 
                            src={order.metaData.gig.coverImg || "https://via.placeholder.com/80x80"} 
                            alt={order.metaData.gig.title}
                            className="mobile-collapsed-image"
                            onError={(e) => {
                                (e.target as HTMLImageElement).src = "https://via.placeholder.com/80x80";
                            }}
                        />
                        <div className="mobile-collapsed-details">
                            <Typography 
                                variant="body1" 
                                className="mobile-collapsed-title"
                                sx={{
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    fontWeight: 500,
                                    fontSize: '0.875rem',
                                    color: '#333',
                                }}
                            >
                                {order.metaData.gig.title}
                            </Typography>
                            <div className="mobile-collapsed-status">
                                <Chip
                                    label={statusInfo.label}
                                    className="mobile-status-chip"
                                    sx={{
                                        backgroundColor: statusInfo.backgroundColor,
                                        color: statusInfo.textColor,
                                        fontWeight: 600,
                                        fontSize: '0.75rem',
                                        height: '24px',
                                    }}
                                />
                                {isBuyerOrder && (
                                    <div className="mobile-delivery-date-section">
                                        <Typography 
                                            variant="caption" 
                                            className="mobile-delivery-date-label" 
                                            sx={{ 
                                                color: deliveryDateLabelInfo.color,
                                                fontSize: '0.7rem',
                                            }}
                                        >
                                            {deliveryDateLabelInfo.label}
                                        </Typography>
                                        <Typography 
                                            variant="caption" 
                                            className="mobile-delivery-date-value"
                                            sx={{ 
                                                color: order.status === OrderStatus.overdue ? '#ef4444' : 'inherit',
                                                fontSize: '0.75rem',
                                                fontWeight: 500,
                                            }}
                                        >
                                            {deliveryDate 
                                                ? `${deliveryDate.day} ${deliveryDate.month} ${deliveryDate.year}`
                                                : order.deliveryTimeStamp 
                                                ? new Date(order.deliveryTimeStamp).toLocaleDateString()
                                                : 'N/A'}
                                        </Typography>
                                    </div>
                                )}
                            </div>
                        </div>
                        <ExpandMoreIcon className="mobile-expand-icon" />
                    </div>
                </div>
            )}

            {/* Mobile Expanded View / Desktop View */}
            <div className={`order-card-body ${isMobile && !isExpanded ? 'mobile-hidden' : ''}`}>
                {isMobile && isExpanded && (
                    <>
                        <div className="mobile-expand-header" onClick={(e) => {
                            e.stopPropagation();
                            setIsExpanded(false);
                        }}>
                            <ExpandLessIcon className="mobile-collapse-icon" />
                            <Typography variant="caption" sx={{ fontSize: '0.75rem', color: '#666' }}>
                                Collapse
                            </Typography>
                        </div>
                        <div className="mobile-card-header">
                            <div className="mobile-header-row">
                                <div className="mobile-header-item">
                                    <Typography variant="caption" className="mobile-header-label">ORDER PLACED</Typography>
                                    <Typography variant="body2" className="mobile-header-value">{orderPlacedDate}</Typography>
                                </div>
                                <div className="mobile-header-item">
                                    <Typography variant="caption" className="mobile-header-label">TOTAL</Typography>
                                    <Typography variant="body2" className="mobile-header-value">{formatCurrencyIndian(totalAmount)}</Typography>
                                </div>
                            </div>
                            <div className="mobile-header-row">
                                <Typography variant="caption" className="mobile-header-label">ORDER # {order.id.substring(order.id.length - 8)}</Typography>
                                <div className="mobile-header-links">
                                    {isBuyerOrder && isCompleted && (
                                        <Typography variant="caption" className="mobile-link-text" onClick={() => {
                                            navigate(`/order-details/${order.id}`);
                                        }}>
                                            View order details
                                        </Typography>
                                    )}
                                    <Typography variant="caption" className="mobile-link-text" onClick={orderSummaryDetailsBtnHandler}>
                                        Order Summary
                                    </Typography>
                                </div>
                            </div>
                        </div>
                    </>
                )}
            {isBuyerOrder && order.status === OrderStatus.readyForDelivery && (
            <div className="warning-text-wrapper">
                <Typography
                    variant="caption"
                    className="warning-text"
                    sx={{
                        fontSize: '0.75rem',
                        fontWeight: 500,
                        fontStyle: 'italic',
                        textAlign: 'center',
                        color: 'red',
                    }}
                >
                    If you do not review the delivery, the order will be marked as completed automatically after 48 hours.
                </Typography>
            </div>)}

                <div className="order-status-section">
                    <div className="status-message-container">
                        <Chip
                            label={statusInfo.label}
                            className="status-chip"
                            sx={{
                                backgroundColor: statusInfo.backgroundColor,
                                color: statusInfo.textColor,
                                fontWeight: 600,
                                fontSize: '0.875rem',
                                height: '32px',
                            }}
                        />
                        {isBuyerOrder && (
                            <div className="delivery-date-section">
                                <Typography 
                                    variant="body2" 
                                    className="delivery-date-label" 
                                    sx={{ color: deliveryDateLabelInfo.color }}
                                >
                                    {deliveryDateLabelInfo.label}
                                </Typography>
                                <Typography 
                                    variant="body1" 
                                    className="delivery-date-value"
                                    sx={{ color: order.status === OrderStatus.overdue ? '#ef4444' : 'inherit' }}
                                >
                                    {deliveryDate 
                                        ? `${deliveryDate.day} ${deliveryDate.month} ${deliveryDate.year}`
                                        : order.deliveryTimeStamp 
                                        ? new Date(order.deliveryTimeStamp).toLocaleDateString()
                                        : 'N/A'}
                                </Typography>
                            </div>
                        )}
                    </div>
                    {isBuyerOrder && order.status === OrderStatus.pending && (
                        <GGButton
                            variant="outlined"
                            startIcon={<EventRepeatOutlinedIcon />}
                            onClick={() => askForProgressReview(order.id)}
                            className="ask-progress-review-button"
                            size="small"
                            disableClickShrink
                            disabled={isAskForProgressReviewBtnDisabled}
                        >
                            Ask for progress review
                        </GGButton>
                    )}
                    {isBuyerOrder && isCompleted && order.review && (
                        <div className="compact-rating-container">
                            <StarRating
                                className="compact-star-rating"
                                disabled={true}
                                value={order.review.rating}
                                showRatingDescription={false}
                            />
                            <GGButton
                                variant="text"
                                onClick={() => setShowReviewModal(true)}
                                className="view-rating-button"
                                size="small"
                                disableClickShrink
                            >
                                View
                            </GGButton>
                        </div>
                    )}
                    {isBuyerOrder && isCompleted && !order.review && (
                        <GGButton
                            variant="outlined"
                            startIcon={<StarBorderIcon />}
                            onClick={() => setShowRatingFormModal(true)}
                            className="rating-pending-button"
                            size="small"
                            disableClickShrink
                        >
                            Rating pending!
                        </GGButton>
                    )}
                    {isBuyerOrder && order.status === OrderStatus.readyForDelivery && (
                        <GGButton
                            variant="outlined"
                            startIcon={<EventRepeatOutlinedIcon />}
                            onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/order-details/${order.id}`);
                            }}
                            className="review-delivery-btn"
                            size="small"
                            disableClickShrink
                        >
                            Review Delivery
                        </GGButton>
                    )}
                </div>

                <div className="order-content">
                    <div className="order-product-info">
                        <img 
                            src={order.metaData.gig.coverImg || "https://via.placeholder.com/120x120"} 
                            alt={order.metaData.gig.title}
                            className="product-image"
                            onError={(e) => {
                                (e.target as HTMLImageElement).src = "https://via.placeholder.com/120x120";
                            }}
                        />
                        <div className="product-details">
                            {!isBuyerOrder && (
                                <div className="buyer-info-section">
                                    <Avatar 
                                        alt="" 
                                        src={order.metaData.buyer.profileImage} 
                                        sx={{ width: 32, height: 32 }}
                                    />
                                    <div className="buyer-details">
                                        <Typography variant="body2" className="buyer-name">
                                            {order.metaData.buyer.fullName}
                                        </Typography>
                                        <Typography variant="caption" className="member-since" color="textSecondary">
                                            Member since {getDateFromTimeStamp(order.metaData.buyer.createdAt)}
                                        </Typography>
                                    </div>
                                </div>
                            )}
                            
                            <Typography variant="body1" className="product-title">
                                {order.metaData.gig.title}
                            </Typography>
                            {isBuyerOrder && (
                                <div className="seller-info-section">
                                    <Typography variant="caption" className="sold-by-label" color="textSecondary">
                                        Sold By
                                    </Typography>
                                    <div className="seller-details-row">
                                        <Avatar 
                                            alt="" 
                                            src={order.metaData.seller.profileImage} 
                                            sx={{ width: 24, height: 24 }}
                                        />
                                        <Typography 
                                            variant="body2" 
                                            className="seller-name"
                                            onClick={() => navigate(`/profile/${order.metaData.seller.buyer}`)}
                                            sx={{ cursor: 'pointer', textDecoration: 'underline' }}
                                        >
                                            {order.metaData.seller.fullName}
                                        </Typography>
                                    </div>
                                </div>
                            )}
                            {!isBuyerOrder && (
                                <div className="amount-credited-section">
                                    <Typography variant="body2" className="amount-text">
                                        Amount to be credited <b>{getGrandTotalInINR()}</b>
                                    </Typography>
                                    <GGButton 
                                        className="details-btn" 
                                        variant="text" 
                                        onClick={orderSummaryDetailsBtnHandler} 
                                        disableRipple
                                        sx={{ padding: 0, minWidth: 'auto', textTransform: 'none' }}
                                    >
                                        <u>Details</u>
                                    </GGButton>
                                </div>
                            )}
                            {isBuyerOrder && (
                                <div className="total-amount-section">
                                    <Typography variant="body2" className="total-amount-text">
                                        Total Amount: <span className="total-amount-value">{getGrandTotalInINR()}</span>
                                    </Typography>
                                </div>
                            )}
                            <GGButton
                                variant="text"
                                className="view-item-button"
                                size="small"
                                onClick={() => {
                                    navigate(`/gig/${order.metaData.gig.id}`);
                                }}
                            >
                                View Gig
                            </GGButton>
                        </div>
                    </div>

                    <div className="order-actions">
                        {!isBuyerOrder && (
                            <GGButton
                                variant="outlined"
                                startIcon={<ChatOutlinedIcon />}
                                onClick={() => {
                                    navigate("/freelancer-dashboard/messages", {
                                        state: { channelId: order.metaData.channelId },
                                    });
                                }}
                                className="action-button"
                                size="small"
                                disableClickShrink
                            >
                                <Typography fontSize={11} fontWeight="500">Go to Chat</Typography>
                            </GGButton>
                        )}
                        {isBuyerOrder && (
                            <GGButton
                                variant="outlined"
                                startIcon={<ChatOutlinedIcon />}
                                onClick={() => {
                                    navigate("/freelancer-dashboard/messages", {
                                        state: { channelId: order.metaData.channelId },
                                    });
                                }}
                                className="action-button"
                                size="small"
                                disableClickShrink
                            >
                                <Typography fontSize={12} fontWeight="600">Go to chat</Typography>
                            </GGButton>
                        )}
                        
                        {isBuyerOrder && isCompleted && (
                            <GGButton
                                variant="outlined"
                                startIcon={<VisibilityOutlinedIcon />}
                                onClick={() => {
                                    navigate(`/order-details/${order.id}`);
                                }}
                                className="action-button"
                                size="small"
                                disableClickShrink
                            >
                                <Typography fontSize={12} fontWeight="600">View Order</Typography>
                            </GGButton>
                        )}
                        {!isBuyerOrder && isCompleted && (
                            <>
                                <GGLoaderButton
                                    variant="outlined"
                                    isLoading={invoiceDownloadLoader}
                                    circularProgressColorHex={"#bcbcbc"}
                                    disableRipple
                                    startIcon={<PictureAsPdfIcon />}
                                    onClick={() => downloadInvoice(order.id)}
                                    className="action-button"
                                    size="small"
                                    colorHex="#ffffff"
                                >
                                    <Typography fontSize={11} fontWeight="bold">Download Invoice</Typography>
                                </GGLoaderButton>
                                <GGLoaderButton
                                    variant="outlined"
                                    isLoading={receiptDownloadLoader}
                                    circularProgressColorHex={"#bcbcbc"}
                                    disableRipple
                                    startIcon={<PictureAsPdfIcon />}
                                    onClick={() => downloadSellerReceipt(order.id)}
                                    className="action-button"
                                    size="small"
                                    colorHex="#ffffff"
                                >
                                    <Typography fontSize={11} fontWeight="bold">Download Receipt</Typography>
                                </GGLoaderButton>
                            </>
                        )}
                        {isBuyerOrder && isCompleted && (
                            <>
                                <GGLoaderButton
                                    variant="outlined"
                                    isLoading={invoiceDownloadLoader}
                                    circularProgressColorHex={"#bcbcbc"}
                                    disableRipple
                                    startIcon={<PictureAsPdfIcon />}
                                    onClick={() => downloadBuyerInvoice(order.id)}
                                    className="action-button"
                                    size="small"
                                    colorHex="#ffffff"
                                >
                                    <Typography fontSize={11} fontWeight="bold">Download Invoice</Typography>
                                </GGLoaderButton>
                                <GGLoaderButton
                                    variant="outlined"
                                    isLoading={receiptDownloadLoader}
                                    circularProgressColorHex={"#bcbcbc"}
                                    disableRipple
                                    startIcon={<PictureAsPdfIcon />}
                                    onClick={() => downloadBuyerReceipt(order.id)}
                                    className="action-button"
                                    size="small"
                                    colorHex="#ffffff"
                                >
                                    <Typography fontSize={11} fontWeight="bold">Download Receipt</Typography>
                                </GGLoaderButton>
                            </>
                        )}
                    </div>
                </div>
            </div>
            <GGModalWindow
                childView={OrderSummaryModalContent}
                open={showOrderSummary}
                title={`Order Summary : ID: #${order.id}`}
                onCloseHandler={closeOrderSummaryModal}
            />
            {isBuyerOrder && order.review && (
                <GGModalWindow
                    childView={<StaticRatings review={order.review} />}
                    open={showReviewModal}
                    title="Rating & Review"
                    onCloseHandler={() => setShowReviewModal(false)}
                />
            )}
            {isBuyerOrder && isCompleted && !order.review && (
                <GGModalWindow
                    childView={RatingsFormContent}
                    open={showRatingFormModal}
                    title="Rate Your Experience"
                    onCloseHandler={() => setShowRatingFormModal(false)}
                />
            )}
        </Box>
        </>
    );
};

export const OrdersPanel = () => {
    const navigate = useNavigate();
    const location = useLocation();
    
    // Initialize tab value from URL search params, default to 0
    const getInitialTabValue = () => {
        const searchParams = new URLSearchParams(location.search);
        const tabParam = searchParams.get('tab');
        if (tabParam === '1') {
            return 1;
        }
        return 0;
    };
    
    const [tabValue, setTabValue] = useState(getInitialTabValue());
    const [sellerOrders, setSellerOrders] = useState<Order[]>([]);
    const [buyerOrders, setBuyerOrders] = useState<Order[]>([]);
    const [isLoadingSeller, setIsLoadingSeller] = useState(false);
    const [isLoadingBuyer, setIsLoadingBuyer] = useState(false);

    useEffect(() => {
        const fetchSellerOrders = async () => {
            setIsLoadingSeller(true);
            try {
                const fetchedOrders = await OrderAPIHandler.shared.getOrdersBySellerId();
                if (fetchedOrders) {
                    setSellerOrders(fetchedOrders);
                }
            } catch (err) {
                ErrorLogger.log(err, 'NewOrdersPanel.fetchSellerOrders');
            } finally {
                setIsLoadingSeller(false);
            }
        };

        fetchSellerOrders();
    }, []);

    useEffect(() => {
        const fetchBuyerOrders = async () => {
            setIsLoadingBuyer(true);
            try {
                const fetchedOrders = await OrderAPIHandler.shared.getOrdersByBuyerId();
                if (fetchedOrders) {
                    setBuyerOrders(fetchedOrders);
                }
            } catch (err) {
                ErrorLogger.log(err, 'NewOrdersPanel.fetchBuyerOrders');
            } finally {
                setIsLoadingBuyer(false);
            }
        };

        if (tabValue === 1) {
            fetchBuyerOrders();
        }
    }, [tabValue]);

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
        // Update URL with tab parameter
        const searchParams = new URLSearchParams(location.search);
        if (newValue === 1) {
            searchParams.set('tab', '1');
        } else {
            searchParams.delete('tab');
        }
        const newSearch = searchParams.toString();
        navigate(`${location.pathname}${newSearch ? `?${newSearch}` : ''}`, { replace: true });
    };
    
    // Sync tab value with URL when location changes (e.g., browser back/forward)
    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const tabParam = searchParams.get('tab');
        const newTabValue = tabParam === '1' ? 1 : 0;
        setTabValue(newTabValue);
    }, [location.search]);

    const renderOrdersList = (orders: Order[], isLoading: boolean, isBuyerOrder: boolean) => {
        if (isLoading) {
            return (
                <div className="orders-skeleton-list">
                    {[...Array(3)].map((_, index) => (
                        <Box key={index} className="skeleton-card">
                            <Skeleton variant="rectangular" width="100%" height={200} className="skeleton-card-content" />
                        </Box>
                    ))}
                </div>
            );
        }

        if (orders.length === 0) {
            return (
                <div className="no-orders-placeholder">
                    <Box className="placeholder-icon">
                        <Inventory2RoundedIcon fontSize="large" />
                    </Box>
                    <Typography variant="h5" className="placeholder-title">
                        No orders available yet
                    </Typography>
                    <Typography variant="body2" className="placeholder-description">
                        {isBuyerOrder 
                            ? "Orders you placed will appear here."
                            : "Orders you fulfilled will appear here once you start fulfilling orders."}
                    </Typography>
                </div>
            );
        }

        return (
            <div className="orders-list">
                {!isBuyerOrder && (
                    <Typography variant="body2" style={{ fontStyle: 'italic' }} className="orders-panel-title">These are the orders you fulfilled as a freelancer. To see the list of orders you bought as a client, click on the <b>Orders(Placed by you)</b> tab.</Typography>
                )}
                {isBuyerOrder && (
                    <Typography variant="body2" style={{ fontStyle: 'italic' }} className="orders-panel-title">These are the orders you bought as a client. To see the list of orders you fulfilled as a freelancer, click on the <b>Orders</b> tab.</Typography>
                )}
                {orders.map((order) => (
                    <OrderCard key={order.id} order={order} isBuyerOrder={isBuyerOrder} />
                ))}
            </div>
        );
    };

    return (
        <section className="new-orders-panel">

            <Box className="orders-tabs-container">
                <Tabs 
                    value={tabValue} 
                    onChange={handleTabChange}
                    className="orders-tabs"
                    sx={{
                        borderBottom: '1px solid #e5e5e5',
                        '& .MuiTab-root': {
                            textTransform: 'none',
                            fontWeight: 500,
                            fontSize: '0.875rem',
                            minHeight: '48px',
                            '&.Mui-selected': {
                                color: '#0066b2',
                                fontWeight: 600,
                            },
                        },
                        '& .MuiTabs-indicator': {
                            backgroundColor: '#0066b2',
                            height: '3px',
                        },
                    }}
                >
                    <Tab label="Orders" />
                    <Tab label="Orders (Placed by you)" />
                </Tabs>
            </Box>
            <div className="new-orders-panel-content">
                <TabPanel value={tabValue} index={0}>
                    {renderOrdersList(
                        sellerOrders.filter(order => order.status === OrderStatus.completed || order.status === OrderStatus.cancelled),
                        isLoadingSeller,
                        false
                    )}
                </TabPanel>
                <TabPanel value={tabValue} index={1}>
                    {renderOrdersList(buyerOrders, isLoadingBuyer, true)}
                </TabPanel>
            </div>
        </section>
    );
};

